/**
 * Pixel de conversão da OpenAI Ads (anúncios no ChatGPT) — Ads Manager →
 * Conversions → data source "fidexa.com.br - site".
 *
 * - Só roda no site público. Na área logada e no backoffice o pixel não
 *   carrega, e se o visitante navegar para lá pela SPA a medição é desligada
 *   (`consent false`): o advanced matching da OpenAI lê dados da página, e ali
 *   há PII de devedores.
 * - Mede todo visitante do site público, sem depender do banner de cookies
 *   (decisão do founder, 2026-10-24).
 * - A conversão é `lead_created`, disparada junto do `form_submit_demo`
 *   (`trackEvent` em `dataLayer.ts`). No Ads Manager ela se chama
 *   "Agendou demo (form)".
 */
import { isAppPath } from "./analyticsConsent";

const PIXEL_ID = "SY6Efj9n97Hoo199xjJWND";
const SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

type Oaiq = ((...args: unknown[]) => void) & { q: unknown[][] };

type OaiqWindow = Window & { oaiq?: Oaiq };

const isTrackedPath = (pathname: string) =>
  !isAppPath(pathname) && !pathname.startsWith("/backoffice");

const isLocalhost = () =>
  ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);

// Mesmo loader do snippet oficial: enfileira as chamadas até o SDK chegar.
const loadPixel = (w: OaiqWindow) => {
  const queue: Oaiq = Object.assign(
    (...args: unknown[]) => {
      queue.q.push(args);
    },
    { q: [] as unknown[][] }
  );
  w.oaiq = queue;

  const script = document.createElement("script");
  script.async = true;
  script.src = SDK_URL;
  const first = document.getElementsByTagName("script")[0];
  first.parentNode?.insertBefore(script, first);

  queue("init", { pixelId: PIXEL_ID });
};

let lastTracked: boolean | null = null;

/** Liga ou desliga o pixel conforme a rota. Chamar no load e a cada navegação. */
export const syncOpenAiPixel = (pathname: string) => {
  if (typeof window === "undefined" || isLocalhost()) return;

  const w = window as OaiqWindow;
  const tracked = isTrackedPath(pathname);
  // O router avisa toda mudança de estado, não só de rota.
  if (tracked === lastTracked) return;
  lastTracked = tracked;

  if (!w.oaiq) {
    if (tracked) loadPixel(w);
    return;
  }
  w.oaiq("consent", tracked);
};

/** Conversão "Agendou demo (form)" na OpenAI Ads. */
export const trackOpenAiLead = () => {
  if (typeof window === "undefined") return;
  (window as OaiqWindow).oaiq?.("measure", "lead_created", {
    type: "customer_action",
  });
};
